import { useCallback, useEffect, useRef, useState } from 'react';

const INTERVAL_MS = 5 * 60 * 1000;

export function useAppUpdate() {
	const [hasUpdate, setHasUpdate] = useState(false);
	const [latestVersion, setLatestVersion] = useState(null);
	const [isChecking, setIsChecking] = useState(false);

	const isMountedRef = useRef(false);
	const isCheckingRef = useRef(false);
	const bootstrapVersionRef = useRef(null); // set on first successful fetch
	const dismissedVersionRef = useRef(null);

	const checkForUpdate = useCallback(async () => {
		if (process.env.NODE_ENV !== 'production' || isCheckingRef.current) return;

		isCheckingRef.current = true;
		if (isMountedRef.current) setIsChecking(true);

		try {
			const baseUrl = process.env.PUBLIC_URL || '';
			const versionUrl = `${baseUrl}/version.json?t=${Date.now()}`;
			const response = await fetch(versionUrl, { cache: 'no-cache' });

			if (!response.ok) return;

			const { version: remoteVersion } = await response.json();

			if (!remoteVersion || remoteVersion === 'unknown') return;

			// First successful fetch: record the version the user currently has loaded.
			// Subsequent fetches compare against this to detect a new deployment.
			if (!bootstrapVersionRef.current) {
				bootstrapVersionRef.current = remoteVersion;
				return;
			}

			if (isMountedRef.current) setLatestVersion(remoteVersion);

			if (
				remoteVersion !== bootstrapVersionRef.current &&
				remoteVersion !== dismissedVersionRef.current &&
				isMountedRef.current
			) {
				setHasUpdate(true);
			}
		} catch (error) {
			console.error('ERROR_CHECKING_FOR_APP_UPDATE:', error);
		} finally {
			isCheckingRef.current = false;
			if (isMountedRef.current) setIsChecking(false);
		}
	}, []);

	const handleVisibilityChange = useCallback(() => {
		if (document.visibilityState === 'visible') checkForUpdate();
	}, [checkForUpdate]);

	useEffect(() => {
		isMountedRef.current = true;

		if (process.env.NODE_ENV !== 'production') {
			return () => {
				isMountedRef.current = false;
			};
		}

		checkForUpdate();

		const intervalId = window.setInterval(checkForUpdate, INTERVAL_MS);
		window.addEventListener('focus', checkForUpdate);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			isMountedRef.current = false;
			window.clearInterval(intervalId);
			window.removeEventListener('focus', checkForUpdate);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [checkForUpdate, handleVisibilityChange]);

	const reloadApp = useCallback(() => {
		window.location.reload();
	}, []);

	const dismissUpdate = useCallback(() => {
		dismissedVersionRef.current = latestVersion;
		setHasUpdate(false);
	}, [latestVersion]);

	return {
		hasUpdate,
		latestVersion,
		isChecking,
		checkForUpdate,
		reloadApp,
		dismissUpdate,
	};
}
